import { useState, useEffect, useMemo } from 'react';
import type { MockInspection } from '@/mocks/qms';
import { calcSampleQty } from '@/mocks/qms';
import { getAllInspectionChecklists } from '@/api/inspectionchecklist.api';
import { getAllOperators } from '@/api/operators.api';
import { getAllItems } from '@/api/item.api';
import { getAllProductionOrders } from '@/api/productionOrder.api';
import { useWarehouseStore } from '@/stores/warehouseStore';
import SearchableSelect, { type SearchableOption } from '@/components/base/SearchableSelect';

interface Props {
  isOpen: boolean;
  onClose: () => void;
  onSave: (inspection: MockInspection) => void;
}

interface ChecklistState {
  id: string;
  name: string;
  applicableTo: string;
  samplingPlan: 'ALL' | 'RANDOM_10' | 'RANDOM_20' | 'AQL';
  isActive: boolean;
  parameters: string[];
}

interface OperatorState {
  id: string;
  name: string;
  employeeCode: string;
  skill: string;
  isActive: boolean;
}

interface ItemState {
  id: string;
  code: string;
}

interface ProductionOrderState {
  id: string;
  poNumber: string;
  productId: string;
  productName: string;
  plannedQty: number;
  completedQty: number;
  status: string;
  unit: string;
  warehouseId?: string;
  isVariant?: boolean;
  variantName?: string | null;
}

export default function NewFinalSlideOver({ isOpen, onClose, onSave }: Props) {
  const selectedWarehouseId = useWarehouseStore((state) => state.selectedWarehouseId);
  const [poId, setPoId] = useState('');
  const [checklistId, setChecklistId] = useState('');
  const [samplingPlan, setSamplingPlan] = useState<'ALL' | 'RANDOM_10' | 'RANDOM_20' | 'AQL'>('ALL');
  const [sampleQty, setSampleQty] = useState(0);
  const [inspectorId, setInspectorId] = useState('');
  const [scheduledDate, setScheduledDate] = useState(() => new Date().toISOString().split('T')[0]);
  const [totalQty, setTotalQty] = useState(0);
  const [unit, setUnit] = useState('');

  // Master data states
  const [productionOrders, setProductionOrders] = useState<ProductionOrderState[]>([]);
  const [checklists, setChecklists] = useState<ChecklistState[]>([]);
  const [operators, setOperators] = useState<OperatorState[]>([]);
  const [items, setItems] = useState<ItemState[]>([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!isOpen) return;
    let active = true;
    async function loadData() {
      try {
        setLoading(true);
        const [poRes, checklistsRes, operatorsRes, itemsRes] = await Promise.all([
          getAllProductionOrders({ warehouseId: selectedWarehouseId || undefined }),
          getAllInspectionChecklists(),
          getAllOperators(),
          getAllItems(selectedWarehouseId || undefined),
        ]);
        if (!active) return;

        if (poRes.success && poRes.data) {
          const mappedPOs: ProductionOrderState[] = poRes.data.map((p) => ({
            id: p.id,
            poNumber: p.po_number,
            productId: p.item_id,
            productName: p.item_name || 'Unknown Product',
            plannedQty: Number(p.planned_qty),
            completedQty: Number(p.completed_qty),
            status: p.status,
            unit: 'Pcs',
            warehouseId: p.warehouse_id,
            isVariant: false,
            variantName: null,
          }));
          setProductionOrders(mappedPOs);
        }

        if (checklistsRes.success && checklistsRes.data) {
          const mappedChecklists: ChecklistState[] = checklistsRes.data.map((c) => ({
            id: c.id,
            name: c.name,
            applicableTo: c.applicable_to,
            samplingPlan: c.sampling_plan as ChecklistState['samplingPlan'],
            isActive: c.is_active,
            parameters: c.parameter_ids || [],
          }));
          setChecklists(mappedChecklists);
        }

        if (operatorsRes.success && operatorsRes.data) {
          const mappedOperators: OperatorState[] = operatorsRes.data.map((o) => ({
            id: o.id,
            name: o.name,
            employeeCode: o.employee_code || '',
            skill: o.skill,
            isActive: o.is_active,
          }));
          setOperators(mappedOperators);
        }

        if (itemsRes.success && itemsRes.data) {
          const mappedItems: ItemState[] = itemsRes.data.map((i) => ({
            id: i.id,
            code: i.code || '',
          }));
          setItems(mappedItems);
        }
      } catch (err) {
        console.error('Failed to load master data in NewFinalSlideOver:', err);
      } finally {
        if (active) setLoading(false);
      }
    }
    loadData();
    return () => {
      active = false;
    };
  }, [isOpen, selectedWarehouseId]);

  const eligiblePOs = useMemo(() => {
    const filtered = productionOrders.filter(
      (po) => !po.status || po.status.toUpperCase() !== 'CANCELLED'
    );
    return filtered.length > 0 ? filtered : productionOrders;
  }, [productionOrders]);

  const finalChecklists = useMemo(() => {
    const filtered = checklists.filter(
      (c) =>
        (!c.applicableTo ||
          c.applicableTo.toUpperCase() === 'FINAL' ||
          c.applicableTo.toUpperCase() === 'ALL') &&
        c.isActive !== false
    );
    return filtered.length > 0 ? filtered : checklists;
  }, [checklists]);

  const qcInspectors = useMemo(() => {
    const filtered = operators.filter(
      (o) =>
        (!o.skill ||
          o.skill.toUpperCase() === 'QC_INSPECTOR' ||
          o.skill.toUpperCase().includes('QC') ||
          o.skill.toUpperCase().includes('INSPECTOR')) &&
        o.isActive !== false
    );
    return filtered.length > 0 ? filtered : operators;
  }, [operators]);

  const poSelectOptions: SearchableOption[] = useMemo(() => [
    { value: '', label: '-- Select Production Order --' },
    ...eligiblePOs.map((po) => ({
      value: po.id,
      label: `${po.poNumber} — ${po.productName} (${po.status})`,
      sublabel: `${po.completedQty}/${po.plannedQty} ${po.unit}`
    }))
  ], [eligiblePOs]);

  const checklistSelectOptions: SearchableOption[] = useMemo(() => [
    { value: '', label: '-- Select Checklist --' },
    ...finalChecklists.map((c) => ({
      value: c.id,
      label: c.name,
      sublabel: `${c.parameters.length} params`
    }))
  ], [finalChecklists]);

  const samplePlanSelectOptions: SearchableOption[] = useMemo(() => [
    { value: 'ALL', label: '100% (All)' },
    { value: 'RANDOM_10', label: 'Random 10%' },
    { value: 'RANDOM_20', label: 'Random 20%' },
    { value: 'AQL', label: 'AQL' }
  ], []);

  const inspectorSelectOptions: SearchableOption[] = useMemo(() => [
    { value: '', label: '-- Select Inspector --' },
    ...qcInspectors.map((o) => ({
      value: o.id,
      label: o.name,
      sublabel: o.employeeCode
    }))
  ], [qcInspectors]);

  useEffect(() => {
    if (!poId) {
      setTotalQty(0);
      setUnit('');
      return;
    }
    const po = productionOrders.find((p) => p.id === poId);
    if (po) {
      setTotalQty(po.completedQty > 0 ? po.completedQty : po.plannedQty);
      setUnit(po.unit);
    }
  }, [poId, productionOrders]);

  useEffect(() => {
    if (!checklistId) return;
    const cl = checklists.find((c) => c.id === checklistId);
    if (cl) {
      setSamplingPlan(cl.samplingPlan);
    }
  }, [checklistId, checklists]);

  useEffect(() => {
    setSampleQty(calcSampleQty(samplingPlan, totalQty));
  }, [samplingPlan, totalQty]);

  const handleSave = () => {
    if (!poId || !checklistId || !inspectorId) return;
    const po = productionOrders.find((p) => p.id === poId)!;
    const checklist = checklists.find((c) => c.id === checklistId)!;
    const inspector = operators.find((o) => o.id === inspectorId)!;
    const item = items.find((i) => i.id === po.productId);

    const nextNum = productionOrders.length + 7;
    const newInspection: MockInspection = {
      id: `insp-${Date.now()}`,
      inspectionNumber: `INSP-2024-00${nextNum}`,
      type: 'FINAL',
      status: 'PENDING',
      triggeredBy: 'MANUAL',
      sourceType: 'PRODUCTION_ORDER',
      sourceId: po.id,
      sourceNumber: po.poNumber,
      itemId: po.productId,
      itemName: po.productName,
      itemCode: item?.code || null,
      isVariant: po.isVariant ?? false,
      variantName: po.variantName || null,
      checklistId: checklist.id,
      checklistName: checklist.name,
      samplingPlan,
      batchNumber: null,
      lotNumber: null,
      totalQty,
      sampleQty,
      passedQty: 0,
      failedQty: 0,
      unit: unit || po.unit,
      inspectorId: inspector.id,
      inspectorName: inspector.name,
      scheduledDate,
      completedDate: null,
      warehouseId: po.warehouseId || selectedWarehouseId || null,
      notes: null,
      createdAt: new Date().toISOString(),
    };
    onSave(newInspection);
    resetForm();
  };

  const resetForm = () => {
    setPoId('');
    setChecklistId('');
    setSamplingPlan('ALL');
    setSampleQty(0);
    setInspectorId('');
    setScheduledDate(new Date().toISOString().split('T')[0]);
    setTotalQty(0);
    setUnit('');
  };

  if (!isOpen) return null;

  const isValid = poId && checklistId && inspectorId;

  return (
    <div className="fixed inset-0 z-50 flex justify-end">
      <div className="absolute inset-0 bg-black/40" onClick={onClose} />
      <div className="relative w-full max-w-lg bg-white h-full shadow-xl flex flex-col animate-slide-in-right">
        <div className="flex items-center justify-between px-5 py-4 border-b border-gray-200">
          <h2 className="text-lg font-semibold text-gray-900">New Final Inspection</h2>
          <button onClick={onClose} className="p-1.5 text-gray-400 hover:text-gray-600 rounded">
            <i className="ri-close-line text-xl" />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto p-5 space-y-5">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Production Order <span className="text-red-500">*</span>
            </label>
            <SearchableSelect
              value={poId}
              onChange={(val) => setPoId(val)}
              options={poSelectOptions}
              placeholder="-- Select Production Order --"
              searchPlaceholder="Search production order..."
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Checklist <span className="text-red-500">*</span>
            </label>
            <SearchableSelect
              value={checklistId}
              onChange={(val) => setChecklistId(val)}
              options={checklistSelectOptions}
              placeholder="-- Select Checklist --"
              searchPlaceholder="Search checklist..."
            />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Sample Plan</label>
              <SearchableSelect
                value={samplingPlan}
                onChange={(val) => setSamplingPlan(val as typeof samplingPlan)}
                options={samplePlanSelectOptions}
                placeholder="Select Sample Plan"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Sample Qty</label>
              <input
                type="number"
                value={sampleQty || ''}
                onChange={(e) => setSampleQty(Number(e.target.value))}
                className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-violet-500"
              />
              <p className="text-xs text-gray-400 mt-1">Auto-calculated from plan</p>
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Inspector <span className="text-red-500">*</span>
            </label>
            <SearchableSelect
              value={inspectorId}
              onChange={(val) => setInspectorId(val)}
              options={inspectorSelectOptions}
              placeholder="-- Select Inspector --"
              searchPlaceholder="Search inspector..."
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Scheduled Date</label>
            <input
              type="date"
              value={scheduledDate}
              onChange={(e) => setScheduledDate(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-violet-500"
            />
          </div>
        </div>

        <div className="px-5 py-4 border-t border-gray-200 flex justify-end gap-3">
          <button
            onClick={onClose}
            className="px-4 py-2 border border-gray-300 rounded-md text-sm text-gray-700 hover:bg-gray-50 whitespace-nowrap"
          >
            Cancel
          </button>
          <button
            onClick={handleSave}
            disabled={!isValid}
            className={`px-4 py-2 rounded-md text-sm font-medium text-white whitespace-nowrap ${
              isValid ? 'bg-violet-600 hover:bg-violet-700' : 'bg-gray-300 cursor-not-allowed'
            }`}
          >
            Create Inspection
          </button>
        </div>
      </div>
    </div>
  );
}